import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  FaPaperPlane,
  FaArrowLeft,
  FaRobot,
  FaUser,
  FaExclamationTriangle,
  FaTrashAlt,
  FaBolt,
  FaDatabase,
  FaArrowRight,
} from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sendChat } from '../../services/chatService';
import { CitePopup, wrapWithCitations } from '../UI/Citations';

/* ---------- tokens ---------- */

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = 'var(--btn-primary)';
const panel = '#24344D';
const borderSoft = 'rgba(255, 255, 255, 0.08)';
const borderStrong = 'rgba(255, 255, 255, 0.16)';

/* ---------- animations ---------- */

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(6, 214, 160, 0.55); }
  50%     { box-shadow: 0 0 0 5px rgba(6, 214, 160, 0); }
`;

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;

/* ---------- layout ---------- */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background:
    radial-gradient(1200px 600px at 15% -10%, rgba(0, 180, 216, 0.08), transparent 60%),
    radial-gradient(900px 500px at 110% 110%, rgba(6, 214, 160, 0.05), transparent 60%),
    var(--color-primary);
  color: var(--text-primary);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
    background-size: 32px 32px;
    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.7), transparent 75%);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1.25rem;
  background: rgba(36, 52, 77, 0.82);
  border-bottom: 1px solid ${borderSoft};
  backdrop-filter: blur(10px);
`;

// Aclara para qué sirve cada chat y ofrece el salto al Agente.
const ModeNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex-wrap: wrap;
  padding: 0.6rem 1.25rem;
  background: rgba(0, 180, 216, 0.06);
  border-bottom: 1px solid ${borderSoft};

  .txt {
    flex: 1;
    min-width: 240px;
    font-size: 0.8rem;
    line-height: 1.45;
    color: var(--text-secondary);
  }
  .txt b { color: var(--text-primary); font-weight: 600; }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    border: 1px solid rgba(0, 180, 216, 0.4);
    font-size: 0.78rem;
    color: var(--btn-primary);
    text-decoration: none;
    transition: background 0.15s ease, color 0.15s ease;

    svg { font-size: 0.7rem; }
    &:hover { background: rgba(0, 180, 216, 0.14); color: var(--text-primary); }
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    border-color: ${borderStrong};
    background: rgba(255, 255, 255, 0.03);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-right: auto;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--alert-success);
  animation: ${pulse} 2.2s ease-in-out infinite;
  flex-shrink: 0;
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-top: 2px;
`;

const Pill = styled.span`
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${accent};
  background: rgba(0, 180, 216, 0.1);
  border: 1px solid rgba(0, 180, 216, 0.3);
  padding: 2px 7px;
  border-radius: 3px;
  white-space: nowrap;
`;

/* ---------- messages ---------- */

const MessagesWrap = styled.main`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 1.25rem 1rem;
  display: flex;
  justify-content: center;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 8px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.14); }
`;

const MessagesInner = styled.div`
  width: 100%;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MsgRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
`;

const MsgMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const MsgIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ isUser }) => (isUser ? 'rgba(255,255,255,0.06)' : 'rgba(0, 180, 216, 0.12)')};
  border: 1px solid ${({ isUser }) =>
    isUser ? 'rgba(255,255,255,0.14)' : 'rgba(0, 180, 216, 0.3)'};
  color: ${({ isUser }) => (isUser ? 'var(--text-secondary)' : accent)};
  font-size: 0.7rem;
`;

const Bubble = styled.div`
  position: relative;
  max-width: 85%;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  font-size: 0.92rem;
  line-height: 1.55;
  border: 1px solid ${borderSoft};

  ${({ isUser, isError }) => {
    if (isError) {
      return css`
        background: rgba(239, 71, 111, 0.08);
        border-color: rgba(239, 71, 111, 0.35);
        color: #ffc9d4;
      `;
    }
    if (isUser) {
      return css`
        background: rgba(0, 180, 216, 0.12);
        border-color: rgba(0, 180, 216, 0.3);
        color: #eafaff;
      `;
    }
    return css`
      background: ${panel};
      color: var(--text-primary);
      border-left: 2px solid ${accent};
      border-radius: 4px 10px 10px 10px;
    `;
  }}

  .md {
    font-family: inherit;
  }

  .md p { margin: 0 0 0.6em; }
  .md p:last-child { margin-bottom: 0; }
  .md h1, .md h2, .md h3, .md h4 {
    margin: 0.8em 0 0.4em;
    color: var(--text-primary);
    font-weight: 600;
    line-height: 1.3;
  }
  .md h1 { font-size: 1.15em; }
  .md h2 { font-size: 1.08em; }
  .md h3 { font-size: 1em; }
  .md ul, .md ol { margin: 0 0 0.6em 1.2em; }
  .md li { margin-bottom: 0.2em; }
  .md a { color: ${accent}; text-decoration: underline; }
  .md blockquote {
    border-left: 2px solid ${accent};
    padding-left: 0.8em;
    margin: 0.5em 0;
    color: var(--text-secondary);
  }
  .md code {
    font-family: ${mono};
    font-size: 0.85em;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.35);
    color: #cfe9f4;
  }
  .md pre {
    font-family: ${mono};
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid ${borderSoft};
    padding: 0.75em;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.5em 0;
  }
  .md pre code { background: transparent; padding: 0; }
  .md table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5em 0;
    font-size: 0.88em;
  }
  .md th, .md td {
    border: 1px solid ${borderSoft};
    padding: 4px 8px;
    text-align: left;
  }
  .md th { background: rgba(255, 255, 255, 0.04); }
  .md strong { color: var(--text-primary); }
`;

/* ---------- typing / loading ---------- */

const TypingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: flex-start;
`;

const TypingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: ${panel};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  padding: 0.6rem 0.9rem;
  border-radius: 4px 10px 10px 10px;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  span {
    width: 6px;
    height: 6px;
    background: ${accent};
    border-radius: 50%;
    animation: ${dotBounce} 1.2s infinite ease-in-out;
  }
  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

/* ---------- quick prompts ---------- */

const QuickPromptsWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 0.6rem;
  margin-top: 0.5rem;
`;

const QuickPrompt = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.7rem 0.85rem;
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  svg { color: ${accent}; flex-shrink: 0; margin-top: 2px; }

  &:hover {
    border-color: rgba(0, 180, 216, 0.35);
    background: rgba(0, 180, 216, 0.06);
    color: var(--text-primary);
    transform: translateY(-1px);
  }
`;

/* ---------- input ---------- */

const InputWrap = styled.form`
  display: flex;
  justify-content: center;
  padding: 0.9rem 1.25rem 1.2rem;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.3));
`;

const InputInner = styled.div`
  width: 100%;
  max-width: 820px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.35rem 0.35rem 0.9rem;
  background: rgba(11, 19, 32, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 12px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: rgba(0, 180, 216, 0.5);
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.1);
  }
`;

const Prefix = styled.span`
  color: ${accent};
  font-family: ${mono};
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  user-select: none;
`;

const Input = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: inherit;
  resize: none;
  padding: 0.55rem 0.25rem;
  min-height: 40px;
  max-height: 160px;
  line-height: 1.45;

  &::placeholder { color: var(--text-disabled); }
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 180, 216, 0.45);
  background: linear-gradient(135deg, rgba(0, 180, 216, 0.3), rgba(0, 180, 216, 0.15));
  color: var(--text-primary);
  border-radius: 9px;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.18s ease, border-color 0.18s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(0, 180, 216, 0.45), rgba(0, 180, 216, 0.22));
    border-color: rgba(0, 180, 216, 0.7);
  }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Footer = styled.div`
  width: 100%;
  max-width: 820px;
  margin: 0.5rem auto 0;
  text-align: center;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const QUICK_PROMPTS = [
  '¿Qué noticias recientes de competidores debería tener en mi radar esta semana?',
  'Comparame mis fortalezas con las de los competidores cargados',
  '¿Cuáles de mis objetivos están en riesgo y qué hago con cada uno?',
  '¿Qué post de redes podría sacar hoy basado en lo que está pasando?',
  '¿Qué brechas detectó la IA en el último insight?',
  'Resumime el mapa político de San Nicolás en este momento',
  '¿Hace cuánto se actualizó el scraping y qué falta cargar?',
  'De los materiales en biblioteca, ¿cuáles son los más usables esta semana?',
];

const INITIAL_MESSAGE = {
  id: 'init',
  isUser: false,
  text:
    'Hola Federico. Soy tu asistente con acceso completo a la plataforma: competidores y sus noticias scrapeadas, insights IA, objetivos del mandato, materiales producidos, redes sociales, encuestas, discursos y resultados electorales. ¿Qué necesitás analizar hoy?',
};

const now = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/* ---------- component ---------- */

const ChatPage = () => {
  const [messages, setMessages] = useState([{ ...INITIAL_MESSAGE, ts: now() }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const openCite = (cite, rag) => {
    setActiveRag(rag || []);
    setActiveCite(cite);
  };

  const isEmpty = useMemo(
    () => messages.length === 1 && messages[0].id === 'init',
    [messages]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const goBack = () => navigate(-1);

  const clearChat = () => {
    if (messages.length <= 1) return;
    setMessages([{ ...INITIAL_MESSAGE, ts: now() }]);
    setLastSummary(null);
  };

  const sendText = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMsg = { id: `u-${Date.now()}`, text: trimmed, isUser: true, ts: now() };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      // Convertimos historial al formato {role, content} esperado por el backend.
      // Excluimos el mensaje "init" del bot y los errores anteriores.
      const history = nextMessages
        .filter((m) => m.id !== 'init' && !m.isError)
        .map((m) => ({
          role: m.isUser ? 'user' : 'assistant',
          content: m.text,
        }));

      const data = await sendChat(history);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          text: data.answer,
          isUser: false,
          ts: now(),
          sources_rag: data.sources_rag || [],
        },
      ]);
      setLastSummary(data.context_summary || null);
    } catch (err) {
      console.error('Error chat:', err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err.message ||
        'No se pudo obtener una respuesta del asistente';
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          text: detail,
          isUser: false,
          isError: true,
          ts: now(),
        },
      ]);
    } finally {
      setIsLoading(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendText(inputValue);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendText(inputValue);
    }
  };

  return (
    <Page>
      <TopBar>
        <IconBtn type="button" onClick={goBack} aria-label="Volver">
          <FaArrowLeft />
        </IconBtn>
        <TitleWrap>
          <Title>
            <StatusDot />
            Asistente Arcana
          </Title>
          <SubTitle>
            <span>ASISTENTE IA</span>
            <Pill>FCH-0001</Pill>
            {lastSummary && (
              <Pill
                title={`Última respuesta usó: ${Object.entries(lastSummary).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ')}`}
              >
                <FaDatabase style={{ marginRight: 4 }} />
                {lastSummary.competidores}c · {lastSummary.noticias}n · {lastSummary.insights}i · {lastSummary.objetivos}o · {lastSummary.materiales}m
              </Pill>
            )}
            {!lastSummary && (
              <Pill style={{ color: 'var(--text-disabled)', borderColor: 'rgba(255,255,255,0.14)' }}>
                <FaDatabase style={{ marginRight: 4 }} /> ctx · server-side · todo en vivo
              </Pill>
            )}
          </SubTitle>
        </TitleWrap>
        <IconBtn
          type="button"
          onClick={clearChat}
          disabled={messages.length <= 1}
          aria-label="Limpiar conversación"
          title="Limpiar conversación"
        >
          <FaTrashAlt />
        </IconBtn>
      </TopBar>

      {/* Los dos chats se parecen y conviene decir cuál conviene cuándo:
          este responde de una; el Agente consulta varias secciones y puede
          preparar cambios, a cambio de tardar más. */}
      <ModeNote>
        <span className="txt">
          <b>Respuestas rápidas</b>, de una sola pasada. Si necesitás algo más completo
          —que cruce varias secciones o prepare un cambio— usá el Agente de la plataforma.
        </span>
        <Link to="/agente" className="cta">
          Ir al Agente <FaArrowRight />
        </Link>
      </ModeNote>

      <MessagesWrap>
        <MessagesInner>
          {messages.map((m) => {
            const handleCite = (cite) => openCite(cite, m.sources_rag);
            return (
              <MsgRow key={m.id} isUser={m.isUser}>
                <MsgMeta>
                  <MsgIconBadge isUser={m.isUser}>
                    {m.isError ? <FaExclamationTriangle /> : m.isUser ? <FaUser /> : <FaRobot />}
                  </MsgIconBadge>
                  <span>{m.isError ? 'Error' : m.isUser ? 'Tú' : 'Asistente'}</span>
                  {m.ts && <span style={{ opacity: 0.6 }}>· {m.ts}</span>}
                  {!m.isUser && m.sources_rag?.length > 0 && (
                    <span style={{ opacity: 0.6 }}>· {m.sources_rag.length} RAG · click en ◇N</span>
                  )}
                </MsgMeta>
                <Bubble isUser={m.isUser} isError={m.isError}>
                  {m.isUser ? (
                    m.text
                  ) : (
                    <div className="md">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={wrapWithCitations(handleCite)}
                      >
                        {m.text}
                      </ReactMarkdown>
                    </div>
                  )}
                </Bubble>
              </MsgRow>
            );
          })}

          {isEmpty && !isLoading && (
            <QuickPromptsWrap>
              {QUICK_PROMPTS.map((p) => (
                <QuickPrompt key={p} type="button" onClick={() => sendText(p)}>
                  <FaBolt size={12} />
                  {p}
                </QuickPrompt>
              ))}
            </QuickPromptsWrap>
          )}

          {isLoading && (
            <TypingRow>
              <MsgMeta>
                <MsgIconBadge>
                  <FaRobot />
                </MsgIconBadge>
                <span>Asistente · pensando</span>
              </MsgMeta>
              <TypingBubble>
                Procesando
                <Dots>
                  <span />
                  <span />
                  <span />
                </Dots>
              </TypingBubble>
            </TypingRow>
          )}

          <div ref={messagesEndRef} />
        </MessagesInner>
      </MessagesWrap>

      <InputWrap onSubmit={onSubmit}>
        <div style={{ width: '100%', maxWidth: 820, margin: '0 auto' }}>
          <InputInner>
            <Prefix>{'>'}</Prefix>
            <Input
              ref={inputRef}
              rows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Escribe tu consulta…"
              disabled={isLoading}
              autoFocus
            />
            <SendButton
              type="submit"
              disabled={isLoading || inputValue.trim() === ''}
              aria-label="Enviar"
            >
              <FaPaperPlane size={14} />
            </SendButton>
          </InputInner>
          <Footer>Enter para enviar · Shift + Enter para nueva línea</Footer>
        </div>
      </InputWrap>
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </Page>
  );
};

export default ChatPage;
